// src/oura/newscan/fixtures/journeySamples.ts
//
// The joined-journey sample businesses (JOURNEY-SPEC v1.1 §1, F4): one list,
// read by both sample stores, so a business has the same id, domain and name
// in the scan draft fixtures (./samples.ts, ./store.ts) and in the onboarding
// sample store (src/services/aeo/onboarding.ts). All of it is invented.
//
//   samplesmile.my   Signed, pitched on the platform (2 outlets, 3 services).
//                    Form adds Invisalign and names Petaling Jaya (a town);
//                    the quote sells 3 outlets; no priority keywords. Walks
//                    prospect -> signed -> form -> first check -> baseline.
//   samplelush.my    Signed, pitched on the Mac: the site read takes the name and the city as
//                    outlets (4), the form names 2, the quote sells 2. The branches list.
//   samplespine.my   Signed, pitched on the Mac: no platform pitch. One outlet.
//   samplesight.my   Signed chain, pitched on the Mac: 24 outlets on the quote
//                    and the site. The first check keeps 9 (cap card).
//   sampletea.my     Signed, Online: login and access request released, no
//                    scan. Its first check waits for brand checks.
//   samplekids.my    Signed, not paid yet: kick-off held, no plan start.
//
// Pure data and two small date helpers: no store, no window.

import type { BuyMode } from "@service/aeo/scanDraftTypes";

/** Where a sample stands. The sample store seeds each one at its `stage`. */
export type JourneyStage =
    /** Scanned, not signed: no onboarding record. */
    | "prospect"
    /** Signed, no payment: no kick-off, no plan start. */
    | "not_paid"
    /** Signed and paid, kick-off sent, form not answered. */
    | "kickoff_sent"
    /** The form is answered: Oura drafts the first AI check. */
    | "form_answered"
    /** Online: form answered, login and access request sent, no scan. */
    | "online_released";

export interface JourneyPerson {
    name: string;
    email: string;
}

export interface JourneyPitch {
    /** The pitch scan's id, also the business row's latest scan while a prospect. */
    assessmentId: string;
    daysAgo: number;
    /** The BD who pitched it. */
    by: string;
    aeoScore: number;
    seoScore: number;
}

export interface JourneySample {
    /** Business id in both stores. A domain, as the onboarding store names a business it created. */
    id: string;
    domain: string;
    name: string;
    category: string;
    /** The list's Where column. */
    city: string;
    stage: JourneyStage;
    /** A pitch run on the platform. Null: measured on the Mac (or never), so none in Oura. */
    pitch: JourneyPitch | null;
    contact: JourneyPerson;
    /** Who the form names for access. */
    accessContact: JourneyPerson;
    quote: { ref: string; planName: string; termMonths: number; outlets: number; areas: string[] };
    /** The outlet areas known at signing (`onboarding.outlet_areas`): the pitched outlets, else the quote's. */
    outletAreas: string[];
    signedDaysAgo: number;
    /** What the form will say (or said): services and outlet areas as the client typed them. */
    form: {
        services: string[];
        areas: string[];
        customerQuestions: string[];
        /** The form's goals, its upcoming plans and anything else, in the client's words, for the business's own vertical. */
        goals: string[];
        plans: string;
        other: string;
        answeredDaysAgo: number;
    };
    buyMode: BuyMode;
    /** Priority keywords the plan already holds (engagement.tracking). */
    planKeywords: string[];
    /** Outlet names the plan already holds (engagement.outlet_names). */
    planOutletNames: string[];
    siteBuilder: "wix" | "wordpress" | "framer" | "other";
}

/** The 24 outlets of the chain, in the site's order (the flagship first). */
export const SIGHT_OUTLETS: Array<{ name: string; town: string }> = [
    { name: "Bangsar", town: "KL" },
    { name: "Mont Kiara", town: "KL" },
    { name: "Damansara Uptown", town: "PJ" },
    { name: "SS15", town: "Subang Jaya" },
    { name: "Section 17", town: "PJ" },
    { name: "Sri Petaling", town: "KL" },
    { name: "USJ Taipan", town: "Subang Jaya" },
    { name: "Setia Alam", town: "Shah Alam" },
    { name: "Kota Damansara", town: "PJ" },
    { name: "Kepong", town: "KL" },
    { name: "Setapak", town: "KL" },
    { name: "Wangsa Maju", town: "KL" },
    { name: "Bukit Jalil", town: "KL" },
    { name: "Taman Tun Dr Ismail", town: "KL" },
    { name: "Desa ParkCity", town: "KL" },
    { name: "Taman Connaught", town: "KL" },
    { name: "SS2", town: "PJ" },
    { name: "Ara Damansara", town: "PJ" },
    { name: "Puchong", town: "Subang Jaya" },
    { name: "Section 7", town: "Shah Alam" },
    { name: "Bukit Tinggi", town: "Klang" },
    { name: "Bandar Sunway", town: "Subang Jaya" },
    { name: "Seri Kembangan", town: "KL" },
    { name: "Cyberjaya", town: "Putrajaya" },
];


export const JOURNEY_SAMPLES: JourneySample[] = [
    {
        id: "samplesmile.my",
        domain: "samplesmile.my",
        name: "Sample Smile Dental",
        category: "Dental clinic",
        city: "Bangsar",
        stage: "form_answered",
        pitch: { assessmentId: "samplesmile-pitch", daysAgo: 12, by: "Dana", aeoScore: 31, seoScore: 55 },
        contact: { name: "Sarah Wong", email: "sarah@samplesmile.my" },
        accessContact: { name: "Kevin Lee", email: "kevin@samplesmile.my" },
        quote: { ref: "OT-2026-SMI-001", planName: "Annual plan", termMonths: 12, outlets: 3, areas: [] },
        outletAreas: ["Bangsar", "Mont Kiara"],
        signedDaysAgo: 3,
        form: {
            services: ["Scaling and polishing", "Teeth whitening", "Braces", "Invisalign"],
            areas: ["Bangsar", "Mont Kiara", "Petaling Jaya"],
            customerQuestions: [
                "How much is Invisalign?",
                "Is Invisalign painful?",
                "Do you take walk-ins?",
                "Is parking easy?",
                "Are you open on Sundays?",
                "Can kids come for check-ups?",
                "How long does whitening take?",
            ],
            goals: ["More Invisalign patients", "Fill the weekday afternoon slots"],
            plans: "A November whitening promo for new patients. A third clinic in Petaling Jaya next year.",
            other: "Most new patients find us on Google Maps. People ask about Invisalign prices on WhatsApp every week.",
            answeredDaysAgo: 1,
        },
        buyMode: "visit",
        planKeywords: [],
        planOutletNames: [],
        siteBuilder: "wordpress",
    },
    {
        // The Lush Clinic as staging drafted it (19 Oct 2026): the site read took the clinic's own
        // name and the city as outlets, the form names two areas, the quote sells 2.
        id: "samplelush.my",
        domain: "samplelush.my",
        name: "The Lush Clinic",
        category: "Aesthetic clinic",
        city: "Mont Kiara",
        stage: "form_answered",
        pitch: null,
        contact: { name: "Admin", email: "admin@samplelush.my" },
        accessContact: { name: "Admin", email: "admin@samplelush.my" },
        quote: { ref: "OT-2026-LUSH-002", planName: "Annual plan", termMonths: 12, outlets: 2, areas: ["Mont Kiara", "Bukit Bintang"] },
        outletAreas: ["Mont Kiara", "Bukit Bintang"],
        signedDaysAgo: 4,
        form: {
            services: ["Acne scar treatment", "Pigmentation treatment", "Laser hair removal", "Facials"],
            areas: ["Mont Kiara", "Bukit Bintang"],
            customerQuestions: ["How much is a facial?", "Is laser hair removal painful?", "Do you have a doctor on site?"],
            goals: ["More acne scar patients", "Be recommended for pigmentation"],
            plans: "A year-end facial promo at both branches.",
            other: "",
            answeredDaysAgo: 1,
        },
        buyMode: "visit",
        planKeywords: [],
        planOutletNames: [],
        siteBuilder: "wordpress",
    },
    {
        id: "samplespine.my",
        domain: "samplespine.my",
        name: "Sample Spine Chiropractic",
        category: "Chiropractic clinic",
        city: "SS15",
        stage: "form_answered",
        pitch: null,
        contact: { name: "Dr Aaron Lim", email: "aaron@samplespine.my" },
        accessContact: { name: "Aaron Lim", email: "aaron@samplespine.my" },
        quote: { ref: "OT-2026-SPI-001", planName: "6-Month plan", termMonths: 6, outlets: 1, areas: ["SS15"] },
        outletAreas: ["SS15"],
        signedDaysAgo: 5,
        form: {
            services: ["Chiropractic adjustment", "Spinal decompression", "Posture assessment"],
            areas: ["SS15"],
            customerQuestions: ["How much is a first visit?", "Does an adjustment hurt?", "Is parking easy?", "Are you open on Saturdays?", "Can pregnant women come?"],
            goals: ["More patients from Subang Jaya", "Be seen online"],
            plans: "A free posture talk for office workers in December.",
            other: "",
            answeredDaysAgo: 2,
        },
        buyMode: "visit",
        planKeywords: [],
        planOutletNames: [],
        siteBuilder: "wix",
    },
    {
        id: "samplesight.my",
        domain: "samplesight.my",
        name: "Sample Sight Optometry",
        category: "Optometrist",
        city: "Bangsar",
        stage: "form_answered",
        pitch: null,
        contact: { name: "Michelle Tan", email: "michelle@samplesight.my" },
        accessContact: { name: "Jason Ng", email: "jason@samplesight.my" },
        quote: { ref: "OT-2026-SIG-001", planName: "Annual plan", termMonths: 12, outlets: 24, areas: SIGHT_OUTLETS.map((o) => o.name) },
        outletAreas: SIGHT_OUTLETS.map((o) => o.name),
        signedDaysAgo: 4,
        form: {
            services: ["Eye test", "Progressive lenses", "Contact lenses", "Myopia control for kids"],
            areas: [],
            customerQuestions: ["How much are progressive lenses?", "Do you do eye tests for kids?", "Are you open late?", "Do you accept insurance?"],
            goals: ["Sell more progressive lenses", "Bring more families in"],
            plans: "A year-end frame and lens promo in every store.",
            other: "Each store manager answers their own Google reviews.",
            answeredDaysAgo: 1,
        },
        buyMode: "visit",
        planKeywords: [],
        planOutletNames: [],
        siteBuilder: "other",
    },
    {
        id: "sampletea.my",
        domain: "sampletea.my",
        name: "Sample Tea Co.",
        category: "Tea store",
        city: "",
        stage: "online_released",
        pitch: null,
        contact: { name: "Nadia Rahman", email: "nadia@sampletea.my" },
        accessContact: { name: "Nadia Rahman", email: "nadia@sampletea.my" },
        quote: { ref: "OT-2026-TEA-001", planName: "6-Month plan", termMonths: 6, outlets: 0, areas: [] },
        outletAreas: [],
        signedDaysAgo: 6,
        form: {
            services: ["Loose leaf tea", "Tea gift sets", "Matcha"],
            areas: [],
            customerQuestions: ["Do you ship to Singapore?", "Is your matcha ceremonial grade?", "Which tea helps with sleep?"],
            goals: ["More online orders", "More repeat customers"],
            plans: "A Raya gift set launch in March.",
            other: "We sell on our own site only, not on Shopee.",
            answeredDaysAgo: 4,
        },
        buyMode: "online",
        planKeywords: [],
        planOutletNames: [],
        siteBuilder: "framer",
    },
    {
        id: "samplekids.my",
        domain: "samplekids.my",
        name: "Sample Kids Dental",
        category: "Dental clinic",
        city: "Sri Hartamas",
        stage: "not_paid",
        pitch: { assessmentId: "samplekids-pitch", daysAgo: 9, by: "YW", aeoScore: 27, seoScore: 49 },
        contact: { name: "Priya Nair", email: "priya@samplekids.my" },
        accessContact: { name: "Priya Nair", email: "priya@samplekids.my" },
        quote: { ref: "OT-2026-KID-001", planName: "6-Month plan", termMonths: 6, outlets: 1, areas: ["Sri Hartamas"] },
        outletAreas: ["Sri Hartamas"],
        signedDaysAgo: 1,
        form: {
            services: ["Kids dental check-ups", "Fluoride treatment", "Braces"],
            areas: ["Sri Hartamas"],
            customerQuestions: ["How much is a kids check-up?", "Is fluoride treatment painful?", "Do you take walk-ins?", "Is there parking?"],
            goals: ["More families from Sri Hartamas"],
            plans: "",
            other: "Parents often ask if they can stay in the room.",
            answeredDaysAgo: 0,
        },
        buyMode: "visit",
        planKeywords: [],
        planOutletNames: [],
        siteBuilder: "wix",
    },
];

export function journeySample(idOrDomain: string): JourneySample | null {
    const key = idOrDomain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/+$/, "");
    return JOURNEY_SAMPLES.find((s) => s.id === key || s.domain === key) ?? null;
}

const DAY = 86_400_000;

/** An ISO time `days` days before now, at 10:00 local, so a sample's dates read the same all day. */
export function daysAgoIso(days: number, now = Date.now()): string {
    const d = new Date(now - days * DAY);
    d.setHours(10, 0, 0, 0);
    // Never in the future: "today" before 10:00 reads as now.
    return new Date(Math.min(d.getTime(), now)).toISOString();
}
